import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilityService } from '@eforall/common';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService, DroplistItem, DroplistWidget } from '@eforall/dashboard';
import { FINAL_PRESENTATION_SCORE_COMPLETE, FINAL_PRESENTATION_SCORE_COMPLETE_LATE, FINAL_PRESENTATION_SCORE_MISSED } from '@interfaces';
import { ReadonlyAccessWidget } from '../../../../../../../ui/widget';
import { AcceleratorAreaService } from '../../../../accelerator-area.service';

@Component({
	selector: 'final-presentation-mark-final-presentation-status.dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		ReadonlyAccessWidget,
		DroplistWidget,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './mark-final-presentation-status.dialog.html',
})
export class FinalPresentationMarkFinalPresentationStatusDialog implements OnInit {
	public dialog: DialogContext<{ accVentureId: number, finalPresentationScore?: number, deckSubmittedUTC?: number }> = inject(MAT_DIALOG_DATA);
	public dialogRef = inject(MatDialogRef<FinalPresentationMarkFinalPresentationStatusDialog>);
	public accAreaService = inject(AcceleratorAreaService);
	public util = inject(UtilityService);
	public dialogService = inject(DialogService);

	public finalDeckDueUTC = computed(() => this.accAreaService.data()?.acc.finalDeckDueUTC);

	public selectedStatusScore = signal<number | undefined>(undefined);
	public errorMessage = signal<string>('');

	saveActionEnabled = signal(false);
	saveActionVisible = signal(false);

	saveAction: DialogAction = {
		id: 'save',
		cssClass: signal(undefined),
		enabled: this.saveActionEnabled,
		visible: this.saveActionVisible,
		text: signal('Save'),
		linkType: 'callback',
		callback: async () => this.selectedStatusScore(),
		willCloseDialog: true,
	};

	cancelAction: DialogAction = {
		id: 'close',
		cssClass: signal(undefined),
		enabled: signal(true),
		visible: signal(true),
		text: signal('Close'),
		linkType: "no-action",
		willCloseDialog: true,
	};

	actions: DialogAction[] = [this.saveAction, this.cancelAction];

	icon = signal('fa-regular fa-hand-pointer');

	public isReadonly = computed(() => !this.accAreaService.canEdit());

	public selectedItem = signal<DroplistItem | undefined>(undefined);
	public readonly statuses = computed<DroplistItem[]>(() => {
		return [{
			uniqueId: 1,
			iconContext: {
				iconClass: 'fa-check',
				iconStyle: 'fa-solid',
				text: 'Mark as Complete',
				hideText: 'never',
			},
			disabled: this.isReadonly(),
			customSort: 1
		},
		{
			uniqueId: 0,
			iconContext: {
				iconClass: 'fa-xmark',
				iconStyle: 'fa-solid',
				text: 'Mark as Missed',
				hideText: 'never',
			},
			disabled: this.isReadonly(),
			customSort: 2
		}];
	});


	ngOnInit(): void {
		const finalDeckDueUTC = this.finalDeckDueUTC();
		if (finalDeckDueUTC) this.saveActionVisible.set(true);

		const finalPresentationScore = this.dialog.data?.finalPresentationScore;
		const statusScore = finalPresentationScore == FINAL_PRESENTATION_SCORE_COMPLETE || finalPresentationScore == FINAL_PRESENTATION_SCORE_COMPLETE_LATE ? 1 : finalPresentationScore == FINAL_PRESENTATION_SCORE_MISSED ? 0 : undefined;
		this.selectedStatusScore.set(finalPresentationScore);
		const status = this.statuses().find(e => e.uniqueId == statusScore);
		this.selectedItem.set(status);


	}


	onViewDroplistChange(selectedItem: DroplistItem | undefined) {
		this.errorMessage.set('');

		if (selectedItem?.uniqueId == 1) { // User selected 'Mark as Complete'
			const deckSubmittedUTC = this.dialog.data?.deckSubmittedUTC;
			//
			//Check if there is a deckSubmittedUTC, if not show error message
			//
			if (!deckSubmittedUTC) {
				this.errorMessage.set('Missing Deck Submitted Date: You must add the date before Marking Complete.');
				this.saveActionEnabled.set(false);
				return;
			}

			const finalDeckDueUTC = this.finalDeckDueUTC();
			//
			// Set the score based on deckSubmittedUTC and finalDeckDueUTC
			//
			// if deckSubmittedUTC <= finalDeckDueUTC = 100
			// if deckSubmittedUTC > finalDeckDueUTC = 50
			if (finalDeckDueUTC && deckSubmittedUTC <= finalDeckDueUTC) this.selectedStatusScore.set(FINAL_PRESENTATION_SCORE_COMPLETE);
			else if (finalDeckDueUTC && deckSubmittedUTC > finalDeckDueUTC) this.selectedStatusScore.set(FINAL_PRESENTATION_SCORE_COMPLETE_LATE);
		}
		else if (selectedItem?.uniqueId == 0) {// User selected 'Mark as Missed'
			this.selectedStatusScore.set(FINAL_PRESENTATION_SCORE_MISSED);
		}

		// Check if the status is unchanged
		const finalPresentationScore = this.dialog.data?.finalPresentationScore;
		const statusScore = finalPresentationScore == FINAL_PRESENTATION_SCORE_COMPLETE || finalPresentationScore == FINAL_PRESENTATION_SCORE_COMPLETE_LATE ? 1 : finalPresentationScore == FINAL_PRESENTATION_SCORE_MISSED ? 0 : undefined;
		this.saveActionEnabled.set(statusScore !== selectedItem?.uniqueId);

	}

}
